import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Route } from 'react-router-dom';
import { APP_ROUTES } from '../shared/config/routes-config';
import { LoadingData } from '../ui/LoadingData';
import { NotFoundData } from '../ui/NotFoundData';
import { Pagination } from '../shared/components/core-ui/pagination/pagination.component';
import { isErrorInfo } from '../shared/interfaces/error-info.interface';
import Modal from '../ui/Model';
import Button, {
  ButtonSize,
  ButtonType
} from '../shared/components/core-ui/button/button.component';
import { formatCellDate } from '../OperationModule/helpers';
import { getHousekeepingOrders } from './data.service';
import { HousekeepingOrderItem, HousekeepingOrderStatus } from './types';

const statusLabel: Record<HousekeepingOrderStatus, string> = {
  new: 'New',
  in_progress: 'In Progress',
  done: 'Done'
};

const statusClassName: Record<HousekeepingOrderStatus, string> = {
  new: 'border-blue-100 bg-blue-50 text-blue-600',
  in_progress: 'border-amber-100 bg-amber-50 text-amber-600',
  done: 'border-emerald-100 bg-emerald-50 text-emerald-600'
};

const formatHousekeepingDate = (date?: string | number | Date) => {
  if (!date) return '—';

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return '—';

  return formatCellDate(parsedDate.getTime());
};

const HouseKeepingListPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [items, setItems] = useState<HousekeepingOrderItem[]>([]);
  const [offset, setOffset] = useState(1);
  const [limit, setLimit] = useState(20);
  const [listCount, setListCount] = useState(0);
  const [selectedImageOrder, setSelectedImageOrder] = useState<HousekeepingOrderItem | null>(null);

  const loadHousekeepingOrders = useCallback(
    (page = offset, perPage = limit) => {
      setLoading(true);
      setError(false);

      getHousekeepingOrders({ offset: page, limit: perPage, status: 'done' }).then((response) => {
        setLoading(false);

        if (isErrorInfo(response)) {
          setError(true);
          setItems([]);
          setListCount(0);
          return;
        }

        setItems(response.data || []);
        setListCount(response.count || 0);
      });
    },
    [limit, offset]
  );

  useEffect(() => {
    loadHousekeepingOrders(1, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summary = useMemo(
    () => ({
      total: listCount,
      newCount: items.filter((item) => item.status === 'new').length,
      inProgressCount: items.filter((item) => item.status === 'in_progress').length,
      doneCount: items.filter((item) => item.status === 'done').length
    }),
    [items, listCount]
  );

  const handleRetryLoad = () => {
    loadHousekeepingOrders(offset, limit);
  };

  const handlePageChange = (newPage: number) => {
    setOffset(newPage);
    loadHousekeepingOrders(newPage, limit);
  };

  const handleItemsPerPage = (itemsPerPage: number) => {
    setLimit(itemsPerPage);
    setOffset(1);
    loadHousekeepingOrders(1, itemsPerPage);
  };

  const selectedImages = selectedImageOrder?.images || [];

  return (
    <div className="min-h-screen bg-creams-50 px-6 py-4">
      <section className="mb-6 flex flex-col gap-4 border-b border-gray-200 pb-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-4xl font-recoleta font-semibold text-neutral-500">
            Housekeeping Orders
          </h1>
          <p className="mt-1 text-base font-gilroy-medium text-neutral-400">
            Showing {summary.doneCount} done orders
          </p>
        </div>
      </section>

      <section className="mt-6 space-y-6">
        {loading ? (
          <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
            <LoadingData />
          </div>
        ) : error ? (
          <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm">
            <p className="text-base font-gilroy-bold uppercase tracking-[0.22em] text-rose-500">
              Housekeeping
            </p>
            <h2 className="mt-3 text-4xl font-recoleta text-neutral-500">
              Unable to load housekeeping orders
            </h2>
            <p className="mt-3 max-w-xl text-lg font-gilroy-medium text-neutral-400">
              The housekeeping queue is temporarily unavailable. Retry to fetch the latest orders.
            </p>
            <Button
              onClick={handleRetryLoad}
              type={ButtonType.primary}
              size={ButtonSize.small}
              className="mt-6"
              classNameButton="px-5 py-3 text-sm leading-4"
            >
              Retry
            </Button>
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-3xl border border-gray-200 bg-white p-10 shadow-sm">
            <NotFoundData />
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="w-full overflow-x-auto">
                <table className="min-w-[1320px] w-full border-separate border-spacing-0">
                  <thead className="sticky top-0 z-10 bg-white">
                    <tr className="border-b border-gray-200 text-neutral-900 [&>th]:py-4 [&>th]:font-gilroy-bold [&>th]:text-lg [&>th]:font-medium [&>th]:uppercase [&>th]:tracking-[0.12em]">
                      <th className="px-4 text-left whitespace-nowrap">Id</th>
                      <th className="px-4 text-left whitespace-nowrap">Unit</th>
                      <th className="px-4 text-left whitespace-nowrap">Project</th>
                      <th className="px-4 text-left whitespace-nowrap">Order Date</th>
                      <th className="px-4 text-left whitespace-nowrap">Created</th>
                      <th className="px-4 text-left whitespace-nowrap">Status</th>
                      <th className="px-4 text-left whitespace-nowrap">Images</th>
                      <th className="px-4 text-left whitespace-nowrap">Assigned to</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const imageCount = item.images?.length || 0;

                      return (
                        <tr key={item.id} className="transition-colors hover:bg-creams-50">
                          <td className="border-t border-gray-100 px-4 py-4 align-center">
                            <p className="text-xl font-gilroy-bold text-neutral-500">
                              {item.id || '—'}
                            </p>
                          </td>

                          <td className="border-t border-gray-100 px-4 py-4 align-center">
                            <div>
                              <p className="text-xl font-gilroy-bold text-neutral-500">
                                {item.unitShortName || item.unitName || '—'}
                              </p>
                              <p className="mt-1 text-lg font-gilroy-medium text-neutral-400">
                                {item.unitName || '—'}
                              </p>
                            </div>
                          </td>

                          <td className="border-t border-gray-100 px-4 py-4 align-center">
                            <div>
                              <p className="text-xl font-gilroy-bold text-neutral-500">
                                {item.projectName || '—'}
                              </p>
                              <p className="mt-1 text-lg font-gilroy-medium text-neutral-400">
                                {item.unitTypeGroup || '—'}
                              </p>
                            </div>
                          </td>

                          <td className="border-t border-gray-100 px-4 py-4 align-center text-xl text-neutral-500">
                            {formatHousekeepingDate(item.orderDate)}
                          </td>

                          <td className="border-t border-gray-100 px-4 py-4 align-center text-xl text-neutral-500">
                            {formatHousekeepingDate(item.createdAt)}
                          </td>

                          <td className="border-t border-gray-100 px-4 py-4 align-center">
                            {item.status ? (
                              <span
                                className={`inline-flex rounded-full border px-3 py-1 text-sm font-gilroy-bold uppercase tracking-[0.12em] ${
                                  statusClassName[item.status]
                                }`}
                              >
                                {statusLabel[item.status]}
                              </span>
                            ) : (
                              <span className="text-xl text-neutral-400">—</span>
                            )}
                          </td>

                          <td className="border-t border-gray-100 px-4 py-4 align-center">
                            <Button
                              onClick={() => setSelectedImageOrder(item)}
                              type={ButtonType.primaryInverted}
                              size={ButtonSize.small}
                              disabled={imageCount === 0}
                              classNameButton="px-4 py-3 text-sm leading-4"
                            >
                              {imageCount > 0 ? `View images (${imageCount})` : 'No images'}
                            </Button>
                          </td>

                          <td className="border-t border-gray-100 px-4 py-4 align-center text-xl font-gilroy-regular text-neutral-500">
                            {item.assigneeName || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {listCount > 0 && (
              <div className="fixed bottom-2 inset-x-0 mx-auto flex w-[92%] items-center justify-center lg:w-[62%]">
                <Pagination
                  itemCounts={listCount}
                  itemsPerPage={limit}
                  offset={offset}
                  handlePageChange={handlePageChange}
                  handleItemsPerPage={handleItemsPerPage}
                />
              </div>
            )}
          </>
        )}
      </section>

      <Modal
        isOpen={Boolean(selectedImageOrder)}
        onClose={() => setSelectedImageOrder(null)}
        containerClassName="lg:h-fit lg:max-h-[88vh] lg:w-[900px]"
        headerContent={
          <div className="text-center font-gilroy-bold text-2xl leading-[100%] text-neutral-500">
            Housekeeping images {selectedImageOrder?.id ? `#${selectedImageOrder.id}` : ''}
          </div>
        }
        footerContent={
          <div className="flex justify-end">
            <Button
              onClick={() => setSelectedImageOrder(null)}
              type={ButtonType.primaryInverted}
              size={ButtonSize.medium}
            >
              Close
            </Button>
          </div>
        }
      >
        <div className="max-h-[65vh] overflow-y-auto pr-1">
          {selectedImages.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {selectedImages.map((imageUrl, index) => (
                <a
                  key={`${imageUrl}-${index}`}
                  href={imageUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="group overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
                >
                  <img
                    src={imageUrl}
                    alt={`Housekeeping order ${selectedImageOrder?.id || ''} image ${index + 1}`}
                    className="h-64 w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                    loading="lazy"
                  />
                </a>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-4 py-10 text-center">
              <p className="text-sm font-gilroy-medium text-neutral-400">
                No images uploaded for this housekeeping order.
              </p>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export const HouseKeepingListModule = () => (
  <Route
    exact
    path={APP_ROUTES.OPERATION_MODULE.CHILDREN?.HOUSE_KEEPING_LIST.PATH}
    component={HouseKeepingListPage}
  />
);
